import React from 'react';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { styled } from '@mui/material/styles';

import Button from '../shared/Button';

const VARIANTS = {
  warning: {
    color: '#f79009',
    bg: 'rgba(247, 144, 9, 0.12)',
    bgWrapper: 'rgba(247, 144, 9, 0.06)',
    icon: WarningAmberIcon,
  },
  error: {
    color: '#d92d20',
    bg: 'rgba(217, 45, 32, 0.12)',
    bgWrapper: 'rgba(217, 45, 32, 0.06)',
    icon: ErrorOutlineIcon,
  },
  success: {
    color: '#12b76a',
    bg: 'rgba(18, 183, 106, 0.12)',
    bgWrapper: 'rgba(18, 183, 106, 0.06)',
    icon: CheckCircleOutlineIcon,
  },
};

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiPaper-root': {
    width: 520,
    borderRadius: 20,
    padding: 12,
  },
}));

const IconWrapperWrapper = styled(Box)(({ bg }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const IconWrapper = styled(Box)(({ bg }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ConfirmationModal = (props) => {
  const {
    open,
    onClose,
    onSubmit,
    title,
    text,
    variant = 'warning',
    closeText = 'Cancel',
    submitText = 'Confirm',
    showRevoke = false,
    revokeChecked,
    onRevokeChange,
    loading,
  } = props;

  const config = VARIANTS[variant];
  const Icon = config.icon;

  return (
    <StyledDialog open={open} onClose={onClose}>
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', top: 16, right: 16 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        <Box>
          <IconWrapperWrapper bg={config.bgWrapper}>
            <IconWrapper bg={config.bg}>
              <Icon sx={{ color: config.color, fontSize: 28 }} />
            </IconWrapper>
          </IconWrapperWrapper>

          <Box flex={1} sx={{ p: '0 12px' }}>
            <Typography fontSize={18} fontWeight={600} mt={2} mb={0.5}>
              {title}
            </Typography>

            <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
              {text}
            </Typography>

            {showRevoke && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={revokeChecked}
                    onChange={onRevokeChange}
                  />
                }
                label="Also revoke active sessions"
              />
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ mt: 3, p: 1, justifyContent: 'space-between', gap: 2 }}>
        <Button type='secondary' onClick={onClose} text={closeText} sx={{ flex: 1 }} />

        <Button
          onClick={onSubmit}
          loading={loading}
          disabled={loading}
          text={submitText}
          sx={{
            minWidth: 160,
            backgroundColor: config.color,
            '&:hover': {
              backgroundColor: config.color,
              opacity: 0.9,
            },
            flex: 1,
          }}
        />
      </DialogActions>
    </StyledDialog>
  );
};

export default ConfirmationModal;
